// Updated: 2026-09-08
import type {
  CommentResponse,
  CraftProjectResponse,
  CraftRoomPageResponse,
  CraftRoomMaterialsPageResponse,
  CraftRoomResponse,
  GetCommentsResponse,
  GetUserCraftProjectsResponse,
  SecureCraftRoomPageResponse,
  UserCraftProjectResponse,
} from '@mrb91002/shared-types'

import { isUsableScreenName } from '../../utils/screenName'
import { API_BASE, SECURE_API_BASE } from '../config'
import { DEFAULT_LISTING_BUCKET, type ListingBucket } from '../listingBuckets'

import { PublicApiError } from './errors'

/**
 * PUBLIC craft room page payload (lambda-main `GET /api/craft-rooms/{screenName}`) — the Visitor_Entry's
 * transport, backing `['craftRoom', screenName]` (react-query-craft-room, Req 12.3).
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`. It is spread
 * CONDITIONALLY — exactly as `fetchCraftProject` below already does, the in-file precedent — so a caller
 * that omits `opts` sends an init object with no `signal` key and makes a byte-identical request to
 * before. The URL, the absent headers, the absent `credentials`, and the success parse are unchanged
 * (Req 12.4). This fetcher is deliberately NOT moved onto `secureFetch` (Req 12.5) — it is unauthenticated
 * and must stay that way.
 *
 * KNOWN, RECORDED, NOT FIXED HERE (Req 15.3): the failure below is a bare `Error` with no numeric
 * `status`, unlike the `PublicApiError` / `ApiError` shapes the rest of `src/api/fetchers` uses. So
 * `shouldRetryQuery` cannot see a 4xx from this read, and a 404 craft room — a screen name that does not
 * exist — retries with backoff as if it were a 5xx. Converting it changes the error contract for every
 * existing caller of this module, including `fetchCraftRoom` and `fetchCraftRoomMaterials` below, which
 * is its own slice.
 */
export async function fetchCraftRoomPage(
  screenName: string,
  opts?: { signal?: AbortSignal },
): Promise<CraftRoomPageResponse> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new Error('Failed to fetch craft room page')
  return resp.json() as Promise<CraftRoomPageResponse>
}

/**
 * PERSONAL craft room page payload (lambda-secure `GET /api/secure/craft-room/{screenName}`) — the
 * Owner_Entry's transport, and the ONLY writer of `['me','craftRoom']` (react-query-craft-room, Req 4.1).
 *
 * `opts.signal` is forwarded from a React Query `queryFn` and spread conditionally, same as the public
 * fetcher above and as `fetchCraftProject` below. A caller that omits `opts` makes a byte-identical
 * request: the URL, the method, the `Authorization: Bearer` header, the `Content-Type`,
 * `credentials: 'include'` and the success parse are all unchanged (Req 12.4). Not moved onto
 * `secureFetch` (Req 12.5).
 *
 * Same recorded-not-fixed status hazard as the public fetcher above: the bare `Error` carries no numeric
 * `status`, so a 401 on this read retries with backoff instead of failing fast (Req 15.3).
 */
export async function fetchSecureCraftRoomPage(
  screenName: string,
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<SecureCraftRoomPageResponse> {
  // DEFENCE IN DEPTH behind `useOwnCraftRoomQuery`'s `enabled` gate, which is where the skip belongs.
  // This exists because `encodeURIComponent` turns a nullish screen name into the LITERAL WORD `null`
  // and then builds a perfectly well-formed URL out of it — the request went out, came back 404, and got
  // retried, with nothing anywhere saying "you asked for a user called null". Failing loudly at the
  // transport means the next caller that loses its gate gets a stack trace naming this function instead
  // of a 404 that reads like a missing craft room.
  if (!isUsableScreenName(screenName)) {
    throw new Error(
      `fetchSecureCraftRoomPage: refusing to request a craft room for a non-name screen name (${String(screenName)})`,
    )
  }
  const resp = await fetch(`${SECURE_API_BASE}/craft-room/${encodeURIComponent(screenName)}`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new Error('Failed to fetch secure craft room page')
  return resp.json() as Promise<SecureCraftRoomPageResponse>
}

export async function fetchCraftRoomMaterials(
  screenName: string,
  lastKey: string | null = null,
): Promise<CraftRoomMaterialsPageResponse> {
  let url = `${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}/materials`
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to fetch craft room materials')
  return resp.json() as Promise<CraftRoomMaterialsPageResponse>
}

export async function fetchCraftRoom(screenName: string): Promise<CraftRoomResponse> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`)
  if (!resp.ok) throw new Error('Failed to fetch craft room')
  const data: CraftRoomPageResponse = await resp.json() as CraftRoomPageResponse
  return data.craftRoom || data
}

/**
 * PUBLIC craft project detail payload (lambda-main `GET /api/craft-projects/{projectId}`).
 *
 * This read is identity-INVARIANT: the handler's signature is
 * `getCraftProjectDetails(_event, …)` and the event is never referenced, `/api` is an `addProxy` with
 * no `authorizationType`, and no `Authorization` header is sent from here. That is what lets the query
 * layer key it `['craftProject', projectId]` with no identity discriminator
 * (react-query-craft-project, design § "Verified finding").
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn` (Req 2.4);
 * it is spread conditionally, so a caller that omits it makes a byte-identical request to before. The
 * URL, the absent query parameters, the absent headers, and the success parsing are unchanged
 * (Req 2.6, 8.1).
 *
 * On failure this now throws a status-bearing `PublicApiError` — imported from
 * `./errors`, NOT redeclared — so the shared retry predicate can read the HTTP status and never retry
 * a 4xx (Req 10.9).
 */
export async function fetchCraftProject(
  projectId: string,
  opts?: { signal?: AbortSignal },
): Promise<CraftProjectResponse> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch craft project', resp.status)
  return resp.json() as Promise<CraftProjectResponse>
}

/**
 * ONE Listing_Bucket of the caller's own craft projects — `GET /api/secure/projects?bucket=`
 * (content-visibility-model Requirements 5.3, 12.1; task 29; BL-265).
 *
 * Task 18 replaced this route's whole-partition query with one `begins_with(GSI3SK, …)` key condition and
 * gave it `?bucket=`, and it filed BL-265 for the consequence: with no selector on the client this page
 * showed the PUBLIC bucket only, so an owner's unlisted, private and archived projects had no surface.
 * `bucket` closes that.
 *
 * Sent EXPLICITLY, always, rather than leaning on the handler's `public` default — so which listing a
 * request asked for is visible in the URL rather than inferred from a missing parameter. An unrecognised
 * value is refused with 400, which is why it is typed `ListingBucket` rather than `string`.
 *
 * `opts.signal` is forwarded so an abandoned navigation is a CANCELLATION rather than a query error, the
 * standing rule for any fetcher a React Query `queryFn` reaches.
 */
export async function fetchUserCraftProjects(
  idToken: string,
  opts?: { bucket?: ListingBucket; signal?: AbortSignal },
): Promise<UserCraftProjectResponse[]> {
  const params = new URLSearchParams({ bucket: opts?.bucket ?? DEFAULT_LISTING_BUCKET })
  const resp = await fetch(`${API_BASE.replace('/api', '/api/secure')}/projects?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch user craft projects', resp.status)
  const data: GetUserCraftProjectsResponse = await resp.json() as GetUserCraftProjectsResponse
  return data.projects || []
}

export async function fetchPublicCraftProjects(limit: number = 20): Promise<CraftProjectResponse[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/public?limit=${limit}`)
  if (!resp.ok) throw new Error('Failed to fetch public craft projects')
  const data = await resp.json() as { projects: CraftProjectResponse[] }
  return data.projects || []
}

export async function fetchProjectComments(projectId: string): Promise<CommentResponse[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`)
  if (!resp.ok) throw new Error('Failed to fetch project comments')
  const data: GetCommentsResponse = await resp.json() as GetCommentsResponse
  return data.comments || []
}
